import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { resolveAvailability } from './resolveAvailability'
import type { AccessAnswer, AccessRule, AccessSources, AuthorizationRule, RecordGate, TprmLevelsSwitch } from './accessRules'
import type { AvailabilityInputs } from './availability'
import type { RoleAuthorizationContext } from './roleAuthorization'

type CheckedAuthorization = Extract<AuthorizationRule, { kind: 'fga' | 'fgaOrRole' }>

const answerFor = (isAllowed: boolean): AccessAnswer => (isAllowed ? 'allowed' : 'denied')

/** The arm in force for the organisation, or nothing while LaunchDarkly has no answer. */
const tprmLevelsArm = <Rule>(rule: TprmLevelsSwitch<Rule>, { isLdReady, ldFlags }: AvailabilityInputs): Rule | undefined => {
  if (!isLdReady) return undefined
  return ldFlags.RELEASE_TPRM_LEVELS === true ? rule.flagOn : rule.flagOff
}

/** `fga` means the surface does not exist for an organisation without the service, so no role confers it. */
const roleAuthorizationAnswer = (authorization: CheckedAuthorization, role: RoleAuthorizationContext): AccessAnswer =>
  authorization.kind === 'fga' ? 'denied' : answerFor(authorization.role(role))

/**
 * The one interpreter of the four states each source reports, for a route and for anything else that
 * is gated. In order:
 *
 * 1. Availability, where the rule declares one: `pending` withholds, `unavailable` denies.
 * 2. `open` allows and `role` answers from the caller's role — neither consults the service.
 * 3. A `record` rule answers from that record's four states, and throws where the caller supplied no
 *    record or one of another entity, since a rule naming a verb without its record answers nothing.
 * 4. Otherwise the organisation context's four states decide, keeping the role arm below enforcement.
 * 5. A check's withheld answer beats denied — only availability turns `unavailable` into a denial —
 *    and a role arm is never read once the source it stands in for is authoritative.
 */
export const resolveAccess = (rule: AccessRule, sources: AccessSources): AccessAnswer => {
  // Availability is a fact about the organisation and the deployment, so it short-circuits: a grant
  // must not reveal a module the organisation does not have.
  if (rule.availability != null) {
    const availability = resolveAvailability(rule.availability, sources.availability)
    if (availability === 'pending') return 'pending'
    if (availability === 'unavailable') return 'denied'
  }

  const { authorization } = rule
  if (authorization.kind === 'tprmLevelsSwitch') {
    const arm = tprmLevelsArm(authorization, sources.availability)
    return arm == null ? 'pending' : resolveAccess({ authorization: arm }, sources)
  }
  // Neither of these consults the Permissions Service, so neither waits on it.
  if (authorization.kind === 'open') return 'allowed'
  if (authorization.kind === 'role') return answerFor(authorization.role(sources.role))

  // A record answers alone: its own states already say whether the service decides, so it never waits on
  // the authz context.
  if (authorization.kind === 'record') {
    const { record } = sources
    // `useIsPermitted`'s overloads make the missing id uncompilable; these guard the `isPermitted`
    // callers, who pair a rule with sources by hand. Withholding would hide the mistake behind a
    // spinner that never resolves.
    if (record == null) {
      throw new Error(`${authorization.entity}.${authorization.verb} was resolved with no record in scope`)
    }
    if (record.entity !== authorization.entity) {
      throw new Error(`${authorization.entity}.${authorization.verb} was resolved against a ${record.entity} answer`)
    }
    switch (record.status) {
      case 'loading':
        return 'pending'
      case 'unavailable':
        return 'unavailable'
      case 'role':
        return answerFor(authorization.role(sources.role))
      case 'resolved': {
        // The verb decides alone: the service accounts for what the role arm stood in for, so reading
        // it here would reopen what the service has revoked.
        const permissions: Readonly<Record<string, boolean>> = record.permissions
        return answerFor(permissions[authorization.verb] === true)
      }
      // Unlike the context switch below, this one is not in tail position: without a default, a new
      // record state would fall through to the organisation context and take the role arm the record
      // exists to override.
      default:
        assertNever(record)
        return 'denied'
    }
  }

  const { authz } = sources
  switch (authz.status) {
    case 'loading':
      return 'pending'
    case 'unavailable':
      return 'unavailable'
    case 'role':
      return roleAuthorizationAnswer(authorization, sources.role)
    case 'resolved':
      // A grant must not open a settings page before the organisation enforces.
      if (authorization.kind === 'fgaOrRole' && !authz.fgaAuthoritative) return answerFor(authorization.role(sources.role))
      return answerFor(authorization.fga({ authz }))
  }
}

/** The collapse for a caller that wants a yes or no: anything withheld reads as no. */
export const isPermitted = (rule: AccessRule, sources: AccessSources): boolean => resolveAccess(rule, sources) === 'allowed'

/** Any one rule that allows opens the page; failing that, a withheld answer still beats a denial. */
export const resolveRecordGate = (gate: RecordGate, sources: AccessSources): AccessAnswer => {
  if ('kind' in gate) {
    const arm = tprmLevelsArm(gate, sources.availability)
    return arm == null ? 'pending' : resolveRecordGate(arm, sources)
  }
  if (!('anyOf' in gate)) return resolveAccess(gate, sources)
  const answers = gate.anyOf.map((rule) => resolveAccess(rule, sources))
  if (answers.includes('allowed')) return 'allowed'
  if (answers.includes('pending')) return 'pending'
  if (answers.includes('unavailable')) return 'unavailable'
  return 'denied'
}
